import { useEffect, useState } from 'react';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.atom';
import { FormulaItemTable } from '@/tools/formula/FormulaItemTable.atom';
import { AdvancedSection } from '@/core/AdvancedParameters.atom';
import { useTranslation } from 'react-i18next';

export interface FormulaItemTableIF extends ValidatingFormComponent<string> {
  component: 'FormulaItemTableFormComponent';
  options: FormulaItemTableOption[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  collapseLabel?: string;
  textEditable?: boolean;
  onTextChange?: (newValue: string, original: FormulaItemTableOption) => void;
  onEditClick?: (key: string) => void;
  onRemoveClick?: (key: string) => void;
  onAdditionalIconClick?: (key: string) => void;
  additionalIcon?: string;
  additionalIconTooltip?: string;
  hasErrors?: boolean;
}

export const FormulaItemTableFormComponent: React.FunctionComponent<FormulaItemTableIF> = (props) => {
  const { t } = useTranslation();

  const {
    options,
    extraClassNames,
    collapsible,
    defaultCollapsed = false,
    collapseLabel,
    tooltip,
    tooltipPlacement,
    tooltipDelay,
    testId,
    wrapperClassNames,
    textEditable,
    onTextChange,
    onEditClick,
    onRemoveClick,
    onAdditionalIconClick,
    additionalIcon,
    additionalIconTooltip,
    hasErrors = false,
  } = props;

  const [expandSection, setExpandedSection] = useState(hasErrors);

  useEffect(() => {
    if (hasErrors) {
      setExpandedSection(true);
    }
  }, [hasErrors]);

  const formulaItemTable = (
    <span className={extraClassNames} data-testid={testId}>
      <FormulaItemTable
        options={options}
        onEditClick={onEditClick}
        onRemoveClick={onRemoveClick}
        onAdditionalIconClick={onAdditionalIconClick}
        additionalIcon={additionalIcon}
        additionalIconTooltip={additionalIconTooltip}
        testId={testId}
        onTextChange={onTextChange}
        textEditable={textEditable}
        hasErrors={hasErrors}
      />
    </span>
  );

  const content = collapsible ? (
    <div data-testid={`${testId}-collapsible`} className={wrapperClassNames}>
      <AdvancedSection
        collapsed={defaultCollapsed}
        alternateName={collapseLabel}
        t={t}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        expandSection={expandSection}
        setExpandedSection={setExpandedSection}
        tooltipDelay={tooltipDelay}>
        {formulaItemTable}
      </AdvancedSection>
    </div>
  ) : (
    formulaItemTable
  );

  return <> {content} </>;
};
